/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { StockService } from '../stock.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { Stock, StockItem, StockPlace } from 'src/app/_model/stock-item';
import { OrderItem, OrderItemType } from 'src/app/_model/order-item';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { OrderItemsService } from 'src/app/_services/order-item/logic/order-item.service';
import { MaterialsService } from 'src/app/_services/materials/logic/materials.service';
import { TourStopsService } from 'src/app/_services/tour-stop/logic/tour-stop.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { BranchTypesService } from 'src/app/_services/branch-types/logic/branch-types.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { KeyValuePersistenceService } from 'src/app/_services/persistence/key-value-persistence/key-value-persistence.service';
import { TourService } from 'src/app/_services/tour/logic/tour.service';
import { Material } from 'src/app/_model/material';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { Servicejob } from 'src/app/_model/servicejob';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { LBSUtils } from 'src/app/_util/lbs-utils';

export type SoldItem = {
  tourId: IMSId;
  amt: number;
  posidx: string;
  isDrivingSale?: boolean;
};

export type CorrectionItem = {
  amt: number;
  matId: number;
};

export type StorageInfoItem = {
  info: string;
  matId: number;
};

export type ReturnItem = {
  rowidx: number;
  comment?: string;
  amtdlv: number;
  unit: string;
  matId?: number;
  type: OrderItemType;
};

const STOCK_CORRECTION_KEY = 'stock_corrections';

type StockCorrection = {
  ref: number;
  corrections: {
    matId: number;
    amt: number;
  }[];
}[];

/**
 * Loads Stock
 */
@Injectable()
export class StockLogicService implements StockService {
  public stock$ = new BehaviorSubject<Stock>(undefined);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);
  public stockCorrections$ = new BehaviorSubject<StockCorrection>([]);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private authenticationService: AuthenticationService,
    private clientStateService: ClientStateService,
    private orderItemsService: OrderItemsService,
    private materialService: MaterialsService,
    private tourStopService: TourStopsService,
    private tourService: TourService,
    private servicejobsService: ServicejobsService,
    private branchesService: BranchesService,
    private branchTypesSerivce: BranchTypesService,
    private keyValuePersistence: KeyValuePersistenceService,
    private appParameter: AppParameterService
  ) {
    this.initialize();
    this.orderItemsService.orderItems$.subscribe({
      next: (orderItems) => this.reloadStock(),
    });
    this.clientStateService.theState.subscribe({
      next: (state) => this.reloadStock(),
    });
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.reloadStock();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Public Function
  public async addUnannouncedReturnItems(returnItems: ReturnItem[]): Promise<
    {
      rowidx: number;
      newPosIdx: string;
      assignedSjobId: number;
    }[]
  > {
    let tourId = this.clientStateService.theState.getValue().tourId;
    let tour = this.tourService.getTourById(tourId);

    let tourStopId = this.clientStateService.theState.getValue().tourStopId;

    const sjobs =
      this.servicejobsService.getServiceJobsForTourStopId(tourStopId);
    const job = LBSUtils.getAnyReturnTransportTypeSjob(sjobs);

    let placeholderMaterial =
      this.materialService.getUnknownMaterialPlaceholder();

    let createdOrderItems: {
      rowidx: number;
      newPosIdx: string;
      assignedSjobId: number;
    }[] = [];

    for (const returnItem of returnItems) {
      let label = returnItem.comment;

      let mat: Material
      if (returnItem.matId != null) {
        mat = this.materialService.getMaterialById({
          imsId: returnItem.matId,
          secondaryId: undefined,
          objRef: 'ims/Material',
        });

        label = mat?.label ?? '';
      }

      let orderItem = OrderItem.fromDto(
        {
          id: undefined,
          secondaryid: undefined,
          label: label,
          localStorageInfo: undefined,
        },
        {},
        {
          material: {
            id: returnItem.matId ? returnItem.matId : placeholderMaterial.id.imsId,
            label: mat ? mat.label : placeholderMaterial.label,
          },
          tour: {
            id: tourId.imsId,
            label: tour.label,
          },
          servicejob: {
            id: job?.id,
            label: job?.label,
          },
          amtord: 0,
          amtdlv: returnItem.amtdlv,
          posvalue: undefined,
          label: label,
          unit: returnItem.unit,
          isalternativearticle: false,
          type: returnItem.type,
        },
        this.appParameter.getFormatLabelForObject('ims/OrderItem')
      );

      let createdOrderItem =
        await this.orderItemsService.updateOrCreateOrderItem(orderItem);
      createdOrderItems.push({
        rowidx: returnItem.rowidx,
        newPosIdx: createdOrderItem.posidx,
        assignedSjobId: job?.getImsId(),
      });
    }

    await this.reloadStock();
    return createdOrderItems;
  }

  public async correctMaterial(corrections: CorrectionItem[]) {
    let stockCorrections: StockCorrection =
      (await this.keyValuePersistence.load(STOCK_CORRECTION_KEY)) || [];
    let curTourId = this.clientStateService.theState.getValue()?.tourId?.imsId;

    let stockCorrection = stockCorrections.find((correction) => {
      return correction.ref === curTourId;
    }) || { ref: curTourId, corrections: [] };

    for (const correctionItem of corrections) {
      let found = false;
      for (const correction of stockCorrection.corrections) {
        if (correction.matId == correctionItem.matId) {
          correction.amt += correctionItem.amt;
          found = true;
        }
      }

      if (!found) {
        stockCorrection.corrections.push({
          matId: correctionItem.matId,
          amt: correctionItem.amt,
        });
      }
    }

    let found = false;
    for (const sCorrection of stockCorrections) {
      if (sCorrection.ref === stockCorrection.ref) {
        sCorrection.corrections = stockCorrection.corrections;
        found = true;
      }
    }

    if (!found) {
      stockCorrections.push(stockCorrection);
    }

    await this.keyValuePersistence.store(
      STOCK_CORRECTION_KEY,
      stockCorrections
    );
    await this.reloadStock();
  }

  public async itemsSold(soldItems: SoldItem[]): Promise<
    {
      posIdx: string;
      newPosIdx: string;
      assignedSjobId: number;
    }[]
  > {
    let createdOrderItems: {
      posIdx: string;
      newPosIdx: string;
      assignedSjobId: number;
    }[] = [];
    for (const soldItem of soldItems) {
      if (soldItem.posidx) {
        let oi = this.orderItemsService.getOrderItemByPosIdxAndTourId(
          soldItem.posidx,
          soldItem.tourId
        );

        if (oi) {
          if (oi.servicejob == undefined) {
            let tourStopId =
              this.clientStateService.theState.getValue().tourStopId;
            oi.amtord = oi.amtord - soldItem.amt;
            await this.orderItemsService.updateOrCreateOrderItem(oi);

            const sjobs =
              this.servicejobsService.getServiceJobsForTourStopId(tourStopId);
            const job = LBSUtils.getAnyNonReturnTransportTypeSjob(sjobs);

            let orderItem = OrderItem.fromDto(
              {
                id: undefined,
                secondaryid: undefined,
                label: oi.label,
                localStorageInfo: undefined,
              },
              {},
              {
                material: {
                  id: oi.material.id.imsId,
                  label: oi.material.label,
                },
                tour: {
                  id: oi.tour.id.imsId,
                  label: oi.tour.label,
                },
                servicejob: {
                  id: job.id,
                  label: job.label,
                },
                amtord: 0,
                amtdlv: soldItem.amt,
                posvalue: oi.posValue,
                isalternativearticle: false,
                isdrivingsale: soldItem.isDrivingSale,
              },
              this.appParameter.getFormatLabelForObject('ims/OrderItem')
            );

            let createdOrderItem =
              await this.orderItemsService.updateOrCreateOrderItem(orderItem);
            createdOrderItems.push({
              posIdx: soldItem.posidx,
              newPosIdx: createdOrderItem.posidx,
              assignedSjobId: job?.getImsId(),
            });
          } else {
            oi.amtdlv = (oi.amtdlv || 0) + soldItem.amt;
            await this.orderItemsService.updateOrCreateOrderItem(oi);
          }
        }
      }
    }

    await this.reloadStock();
    return createdOrderItems;
  }

  public getCorrectionWeight(orderItems: OrderItem[]): number {
    let curTourId = this.clientStateService.theState.getValue()?.tourId?.imsId;

    let stockCorrection = this.stockCorrections$
      .getValue()
      .find((correction) => {
        return correction.ref === curTourId;
      }) || { ref: curTourId, corrections: [] };

    let correctionWeight = 0;
    for (const correction of stockCorrection?.corrections) {
      let matWeight =
        orderItems
          ?.find((oi) => oi?.material?.id?.imsId == correction.matId)
          ?.getWeightOfArticle() || 0;

      correctionWeight += matWeight * correction?.amt || 0;
    }

    return correctionWeight;
  }
  // Resetable

  public async reset() {
    this.keyValuePersistence.delete(STOCK_CORRECTION_KEY);
    this.stockCorrections$.next([]);
  }
  public async clear() {
    this.keyValuePersistence.delete(STOCK_CORRECTION_KEY);
    this.stockCorrections$.next([]);
  }

  // Private Functions
  private async reloadStock() {
    try {
      const orderItems = this.orderItemsService.orderItems$.getValue();
      const curTourId = this.clientStateService.theState.getValue()?.tourId;

      let stockCorrections: StockCorrection =
        (await this.keyValuePersistence.load(STOCK_CORRECTION_KEY)) || [];
      let stockCorrection = stockCorrections.find((correction) => {
        return correction.ref === curTourId?.imsId;
      }) || { ref: curTourId?.imsId, corrections: [] };

      this.logger.log(
        this.constructor.name,
        'reload stock with',
        orderItems?.length,
        'orderitems and for tourid',
        curTourId
      );

      let filterdOrederitems: OrderItem[] = orderItems.filter((oi) => {
        return oi?.tour?.id?.imsId === curTourId?.imsId;
      });

      let stockItems: StockItem[] = filterdOrederitems.reduce(
        (
          accumulator: StockItem[],
          currentValue: OrderItem,
          currentIndex: number,
          array: OrderItem[]
        ) => {
          const item = accumulator.find(
            (stockItem) =>
              stockItem.material?.id?.imsId === currentValue.material.id.imsId
          );

          if (item && !item.material.isPlaceholderMaterial) {
            item.orderItems.push(currentValue);
          } else {
            let materialObject: Material = this.materialService.getMaterialById(
              currentValue.material.id
            );

            if (materialObject == undefined) {
              materialObject =
                this.materialService.getUnknownMaterialPlaceholder();
            }

            let correction = stockCorrection.corrections.find(
              (correctionItem) => {
                return correctionItem.matId === currentValue.material.id.imsId;
              }
            );
            accumulator.push(
              new StockItem({
                label: currentValue.material.label,
                material: materialObject,
                orderItems: [currentValue],
                correction: correction?.amt,
              })
            );
          }
          return accumulator;
        },
        []
      );

      this.logger.log(
        this.constructor.name,
        '- Loaded local StockItems',
        stockItems.length
      );

      const appStockPlace = new StockPlace(
        Stock.localAppStockId,
        'AppStock',
        stockItems
      );

      this.logger.log(
        this.constructor.name,
        '- Loaded local StockPlace',
        appStockPlace.id,
        appStockPlace.name,
        appStockPlace.stockItems?.length
      );

      const stock = new Stock([appStockPlace]);

      this.stock$.next(stock);
      this.stockCorrections$.next(stockCorrections);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local stock: ',
        error.message
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {}
}
